<template>
  <div class="responsive-container relative w-full">
    <div v-if="showDefault" ref="wrapper" class="size-full">
      <slot />
    </div>
    <svg
      v-if="showSvg"
      :viewBox="`0 0 ${baseWidth} ${height}`"
      preserveAspectRatio="xMinYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <foreignObject x="0" y="0" width="100%" height="100%">
        <div
          class="size-full"
          :class="{
            flex: align,
            'justify-center': align !== 'top',
            'items-center': align === 'center' || align === 'middle',
            'items-end': align === 'bottom',
          }"
        >
          <div
            ref="innerWrapper"
            class="size-full overflow-auto"
            :style="{
              '--responsive-zoom': responsiveZoom,
            }"
          >
            <slot />
          </div>
        </div>
      </foreignObject>
    </svg>
  </div>
</template>

<script lang="ts">
import { useDeviceType } from '~/composables/useDeviceType'

export default defineComponent({
  name: 'Responsive',
  props: {
    baseWidth: {
      type: Number as PropType<number>,
      required: true,
    },
    initialHeight: {
      type: Number as PropType<number>,
      default: 0,
    },
    ratio: {
      type: String as PropType<string>,
      default: '',
    },
    align: {
      type: String as PropType<'top' | 'center' | 'middle' | 'bottom'>,
      default: undefined,
    },
  },
  setup(props) {
    const wrapper = ref<HTMLDivElement | null>(null)
    const innerWrapper = ref<HTMLDivElement | null>(null)
    const { isMobile } = useDeviceType()
    const height = ref(props.ratio || isMobile.value ? calculateHeight() : 0)
    const showSvg = ref(height.value > 0)
    const showDefault = ref(!showSvg.value)
    const initialized = ref(false)
    const responsiveZoom = ref(1)

    function calculateHeight() {
      if (props.ratio) {
        const [widthRatio, heightRatio] = props.ratio.split('/')
        if (!widthRatio || !heightRatio) return props.initialHeight

        const width = parseInt(widthRatio)
        const ratioHeight = parseInt(heightRatio)
        if (
          !Number.isFinite(width) ||
          !Number.isFinite(ratioHeight) ||
          !width
        ) {
          return props.initialHeight
        }

        const ratio = ratioHeight / width
        return Math.round(props.baseWidth * ratio)
      }
      return props.initialHeight
    }

    const update = () => {
      initialized.value = true
      if (window.innerWidth > props.baseWidth) {
        height.value = 0
        showSvg.value = false
        showDefault.value = true
        responsiveZoom.value = 1
        return
      }

      responsiveZoom.value = window.innerWidth / props.baseWidth
      showSvg.value = true
      height.value = calculateHeight()
      if (height.value > 0) showDefault.value = false
    }

    if (import.meta.client) {
      const debouncedUpdate = useDebounceFn(update, 50)
      window.addEventListener('resize', debouncedUpdate, { passive: true })
      useWatch(innerWrapper, debouncedUpdate)
      onMounted(debouncedUpdate)
    }

    useWatch(() => props.baseWidth, update)

    onUnmounted(() => {
      window.removeEventListener('resize', update)
    })

    return {
      wrapper,
      innerWrapper,
      height,
      showSvg,
      showDefault,
      initialized,
      responsiveZoom,
    }
  },
})
</script>

<style lang="scss">
.responsive-container {
  .not-responsive {
    zoom: calc(1 / var(--responsive-zoom));
  }
}
</style>
